import React, {AnchorHTMLAttributes, ReactNode} from 'react'
import {css} from '@emotion/react'
import styled from '@emotion/styled'
import {SizesType} from 'types/Sizes'
import {Colors, ThemeType} from 'types/Theme'

import {hexToRGBAString} from '@daedalus/core/src/utils/string'

import {mq} from '../../../utils/breakpoints'

export type ButtonLinkSize = Extract<SizesType, 'xs' | 'sm' | 'md'>
export type ButtonLinkVariant = keyof Colors['buttonLink']

interface Props extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Pass through className to allow styles overrides */
  className?: string
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Size variant. If no size is provided, font-size inherited from surrounding text. */
  size?: ButtonLinkSize
  /** Whether the ButtonLink should have a Default, Info, Strong or Inverse variant. */
  variant?: ButtonLinkVariant
  /** An Icon to display on the left side of the Link for LTR languages. Inverted on RTL */
  iconStart?: ReactNode
  /** An Icon to display on the right side of the Link for LTR languages. Inverted on RTL */
  iconEnd?: ReactNode
  /** Whether the link should be compact or not */
  isCompact?: boolean
  /** Whether the font-weight is bold or not */
  isBold?: boolean
  /** Whether the underline is dotted or not */
  isDotted?: boolean
}

interface StyleProps {
  theme: ThemeType
  isDotted?: boolean
  variant: ButtonLinkVariant
}

interface SizeProps {
  theme: ThemeType
  size: TextSizeMapKeys
  isCompact?: boolean
  isBold?: boolean
}

const textSizeMap = {
  xs: 'bodyXS',
  sm: 'bodyS',
  md: 'bodyM'
} as const

const boldTextSizeMap = {
  xs: 'labelXS',
  sm: 'labelS',
  md: 'labelM'
} as const

type TextSizeMapKeys = Extract<SizesType, 'xs' | 'sm' | 'md'>

export const ButtonLinkMinHeightMap: Record<ButtonLinkSize, number> = {
  xs: 20,
  sm: 26,
  md: 30
} as const

export const ButtonLinkSizesMap: Record<ButtonLinkSize, number> = {
  xs: 26,
  sm: 34,
  md: 42
} as const

const withSize = ({size, theme, isCompact = false}: SizeProps) => {
  const minSize = ButtonLinkSizesMap[size]
  const minHeightCompact = ButtonLinkMinHeightMap[size]

  if (isCompact) {
    return css`
      padding: ${theme.layout.spacing.s100}px 0px;
      min-height: ${minHeightCompact}px;
    `
  }

  return css`
    padding: 0 ${theme.layout.spacing.s300}px;
    min-height: ${minSize}px;
    min-width: ${minSize}px;
  `
}

const withTextSizes = ({theme, size, isBold}: SizeProps) => {
  if (size) {
    return css`
      ${!isBold
        ? theme.typography.text[textSizeMap[size]]
        : theme.typography.text[boldTextSizeMap[size]]}
    `
  }

  return css`
    font-size: inherit;
    line-height: inherit;
  `
}

const withUnderline = ({theme, isDotted}: StyleProps) => {
  if (isDotted) {
    return css`
      text-decoration: underline;
      text-decoration-style: dotted;
      text-underline-offset: ${theme.layout.spacing.s200}px;

      ${mq.desktopXs(css`
        &:hover,
        :focus {
          text-decoration-style: dotted;
        }
      `)}

      &:active {
        text-decoration-style: dotted;
      }
    `
  }
  return css`
    text-decoration: underline;
    text-underline-offset: ${theme.layout.spacing.s200}px;
  `
}

const withColors = ({theme, variant}: StyleProps & {theme: ThemeType}) => {
  return css`
    color: ${theme.colors.buttonLink[variant].content};

    ${mq.desktopXs(css`
      &:hover,
      :focus {
        color: ${theme.colors.buttonLink[variant].hover.content};
      }
    `)}

    &:active {
      color: ${theme.colors.buttonLink[variant].active.content};
      background-color: ${variant === 'inverse'
        ? hexToRGBAString(
            theme.colors.buttonLink.inverse.active.background,
            0.15
          )
        : theme.colors.buttonLink[variant].active.background};
    }

    &:visited {
      color: ${theme.colors.buttonLink.default.content};
    }
  `
}

const ButtonLinkWrapper = styled.a<{
  size: TextSizeMapKeys
  variant: ButtonLinkVariant
  isCompact?: boolean
  isDotted?: boolean
}>(
  ({theme}) => css`
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    font-size: inherit;
    line-height: inherit;
    border-radius: ${theme.layout.radius.sm}px;
  `,
  withUnderline,
  withColors,
  withSize
)

export const TextWrapper = styled.span<{
  size: TextSizeMapKeys
  isBold: boolean
}>(
  withTextSizes,
  () => css`
    user-select: none;
    ${mq.desktopXs(
      css`
        user-select: auto;
      `
    )}
  `
)

export const IconWrapper = styled.span(
  ({theme}) => css`
    display: inline-flex;
    align-items: center;
    &:first-child {
      margin-right: ${theme.layout.spacing.s100}px;
    }
    &:last-child {
      margin-left: ${theme.layout.spacing.s100}px;
    }
  `
)

export const ButtonLink = ({
  children,
  size = 'sm',
  variant = 'strong',
  isCompact = false,
  dataId = '',
  iconEnd,
  iconStart,
  className,
  isBold = true,
  isDotted = false,
  ...anchorProps
}: Props) => (
  <ButtonLinkWrapper
    data-id={dataId}
    variant={variant}
    size={size}
    isCompact={isCompact}
    className={className}
    isDotted={isDotted}
    {...anchorProps}
  >
    {iconStart && <IconWrapper>{iconStart}</IconWrapper>}
    <TextWrapper size={size} isBold={isBold}>
      {children}
    </TextWrapper>
    {iconEnd && <IconWrapper>{iconEnd}</IconWrapper>}
  </ButtonLinkWrapper>
)
